<template>
  <panel-layout
    id="reservationStatusType-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="reservationStatusType" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ reservationStatusType.isNewRecord ? $t('reservation.newStatusType') : reservationStatusType.name }}
          </h2>
          <div v-if="!reservationStatusType.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.appointmentStatusType.id.id', { id: reservationStatusType.idReservationStatusType })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="reservationStatusType" ref="form" class="h-full w-full">
      <o-scroll class="h-full">
        <div class="w-full px-6 py-4">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')" :info="$t('pages.appointmentStatusType.id.label')">
              <text-field v-model="reservationStatusType.name" :label="$t('general.title')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" class="w-full" rules="required" :label="$t('general.shortName')" :info="$t('pages.appointmentStatusType.id.shortNameInfo')">
              <text-field v-model="reservationStatusType.shortName" :label="$t('general.shortName')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group
              class="w-full"
              rules="required"
              :label="$t('general.colour')"
              :name="$t('general.colour')"
              :info="$t('pages.appointmentStatusType.id.colourInfo')"
              :validation-value="reservationStatusType.colour"
            >
              <div class="flex flex-wrap -mx-2">
                <div
                  v-for="(colour, key) in colours"
                  :key="key"
                  class="p-2 rounded-xl relative transform transition hover:scale-104 cursor-pointer"
                  :style="{
                    backgroundColor: reservationStatusType.colour === Number(key) ? `#${colour}2e` : '',
                  }"
                  @click="reservationStatusType.colour = Number(key)"
                >
                  <div
                    :style="{
                      backgroundColor: '#' + colour,
                    }"
                    class="w-10 h-10 rounded-lg border"
                  />

                  <o-icon
                    v-if="reservationStatusType.colour === key"
                    icon="check"
                    class="hidden top-1/2 left-1/2 absolute transform -translate-x-1/2 -translate-y-1/2"
                    fill
                    :style="{
                      color: '#' + colour
                    }"
                  />
                </div>
              </div>
            </o-field-group>
          </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="reservationStatusType">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import { TextField, ColourField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const props = defineProps<{ id?: string }>()

const panel = usePanel()
const confirmDialog = ref<any>(null)
const form = ref<any>()

const reservationStatusType = ref<any>(null)
const errors = ref(false)
const loading = ref(true)
const colours = ref<Record<number, string>>({
  1: '999999',
  2: '468847',
  3: 'F89406',
  4: 'B94A48',
  5: '3A87AD',
  6: '333333',
})

const _colour = computed({
  get () {
    return '#' + reservationStatusType.value.hexColour
  },
  set (value: string) {
    reservationStatusType.value.hexColour = value.replace('#', '')
  },
})

const { unsavedChanges, snapshot } = useUnsavedChanges(reservationStatusType)

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      reservationStatusType.value = (await $api.reservationStatusType.defaults({} as any)).data
    } else {
      reservationStatusType.value = (await $api.reservationStatusType.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

async function save (close = false) {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _reservationStatusType = (await $api.reservationStatusType.save(reservationStatusType.value)).data

    if (_reservationStatusType) {
      reservationStatusType.value = _reservationStatusType
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.appointmentStatusType.id.success'),
        text: $i18n.t('pages.appointmentStatusType.id.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ReservationStatusType', _reservationStatusType)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

function cancel (force = false) {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

defineExpose({ save, cancel, unsavedChanges })
</script>
